import { PieChartComponent } from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewTraffic/PieChartComponent";
import type { FC } from "react";
import { useMemo } from "react";
import { CHART_COLORS } from "app/constants/ChartColors";
import type { IWidgetSingleWrapperProps } from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewTraffic/Types";
import { getSingleOrganicVSPaid, getMMXTrafficData } from "app/services/WebsiteAnalysis/SearchOverviewApiService";
import type { IWidgetModelTypesWebSource } from "app/components/widget/widget-types/Widget";
import { AddToDashboard } from "app/components/dashboard/AddToDashboard/AddToDashboard";
import { useQuery } from "@tanstack/react-query";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { channels } from "app/pages/website-analysis/traffic-sources/search/tabs/SearchOverviewGraph/SearchTrafficInsights/typesAndConstants";

const ORGANIC = "Organic";
const PAID = "Paid";

export const OrganicVSPaidWidgetWrapper: FC<IWidgetSingleWrapperProps> = (props) => {
    const { apiParams, apiParamsObj, subtitleFilters, addMarginRight } = props;
    const { webSource, key } = apiParams;
    const isDesktop = webSource === devicesTypes.DESKTOP;
    const tooltipsMap = {
        [devicesTypes.DESKTOP]: "analysis.source.search.overview.organicpaid.tooltip",
        [devicesTypes.MOBILE]: "analysis.source.search.overview.organicpaid.mobile.tooltip",
        [devicesTypes.TOTAL]: "analysis.source.search.overview.organicpaid.alltraffic.tooltip",
    };

    const transformData = (data) => {
        const totalData = data.Data[webSource][key];
        const parsedData = {
            [key]: { [ORGANIC]: totalData[channels.ORGANIC], [PAID]: totalData[channels.PAID] },
        };
        return {
            Data: parsedData,
        };
    };

    const { data, isLoading, isError } = useQuery({
        queryKey: [`${webSource}SearchOverviewOrganicVSPaidWidget`, ...Object.values(apiParams)],
        select(dto) {
            return isDesktop ? dto : transformData(dto);
        },
        queryFn: () => {
            return isDesktop ? getSingleOrganicVSPaid(apiParamsObj) : getMMXTrafficData(apiParamsObj);
        },
    });

    const a2d = () => (
        <AddToDashboard
            webSource={webSource as IWidgetModelTypesWebSource}
            modelType="fromWebsite"
            metric="SearchOrganicPaidOverviewV2"
            type="SearchOrganicPaidOverviewPieChart"
        />
    );

    const chartData = useMemo(() => {
        const dataObj = data?.Data?.[key];
        if (!dataObj) {
            return null;
        } else {
            const allDevices = dataObj && dataObj[ORGANIC] + dataObj[PAID];
            return [
                {
                    data: [
                        {
                            color: CHART_COLORS.DeduplicatedAudience[0],
                            name: ORGANIC,
                            seriesName: ORGANIC,
                            y: (dataObj && dataObj[ORGANIC] / allDevices) * 100,
                        },
                        {
                            color: CHART_COLORS.DeduplicatedAudience[1],
                            name: PAID,
                            seriesName: PAID,
                            y: (dataObj && dataObj[PAID] / allDevices) * 100,
                        },
                    ],
                },
            ];
        }
    }, [data]);

    return (
        <PieChartComponent
            isError={isError}
            isLoading={isLoading}
            chartData={chartData}
            params={apiParams}
            subtitleFilters={subtitleFilters}
            a2d={a2d}
            title={"analysis.source.search.overview.organic.paid"}
            tooltip={tooltipsMap[webSource]}
            addMarginRight={addMarginRight}
        />
    );
};
